import React, { createContext, useContext, useEffect, useState } from 'react';

/**
 * AccessibilityModeContext — single source of truth for accessibility "mode".
 * Modes: 'standard' | 'senior' | 'easy'
 * Persists in localStorage, applies <html> class hooks for global CSS overrides.
 */

const AccessibilityModeContext = createContext({
  mode: 'standard',
  setMode: () => {},
});

const STORAGE_KEY = 'cn-accessibility-mode';
const VALID = ['standard', 'senior', 'easy'];

export const AccessibilityModeProvider = ({ children }) => {
  const [mode, setModeState] = useState(() => {
    if (typeof window === 'undefined') return 'standard';
    const saved = localStorage.getItem(STORAGE_KEY);
    return VALID.includes(saved) ? saved : 'standard';
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('mode-standard', 'mode-senior', 'mode-easy', 'elder-mode');
    root.classList.add(`mode-${mode}`);
    if (mode === 'senior') root.classList.add('elder-mode'); // back-compat
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const setMode = (next) => {
    if (VALID.includes(next)) setModeState(next);
  };

  return (
    <AccessibilityModeContext.Provider value={{ mode, setMode }}>
      {children}
    </AccessibilityModeContext.Provider>
  );
};

export const useAccessibilityMode = () => useContext(AccessibilityModeContext);
